"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { FiSettings, FiEdit3, FiCalendar } from "react-icons/fi"
import FormInput from "../../../ui/form-input"
import FormCheckbox from "../../../ui/form-checkbox"
import api from "../../../libs/axios"
import toast from "react-hot-toast"

interface Props {
  onComplete: (data: any) => void
  onBack: () => void
  onEditBasicInfo: () => void // New prop to redirect to first step
  matchData: any
}

// Statuses in which balls are being scored: a rule change then applies only to balls
// recorded after it (DECISION-007 B4).
const LIVE_STATUSES = new Set([
  "live", "first_innings", "innings_break", "second_innings",
  "super_over_break", "super_over_1", "super_over_2", "paused", "break",
])

// The rule fields this step edits, and the server's own defaults for them (match model).
// The old UI defaults (wide 2, count-as-legal on) silently changed a match's rules when
// the match row did not carry them.
const RULE_DEFAULTS = {
  wide_runs: 1,
  count_wide_as_legal: false,
  no_ball_runs: 1,
  count_no_ball_as_legal: false,
  ignore_wide_noball_last_overs: 2,
}

export default function PreviewRules({ onComplete, onBack, onEditBasicInfo, matchData }: Props) {
  const [formData, setFormData] = useState({
    free_hit_enabled: matchData.free_hit_enabled ?? false,
    super_over_enabled: matchData.super_over_enabled ?? false,
    last_batter_rule: matchData.last_batter_rule ?? false,

    wide_runs: matchData.wide_runs ?? RULE_DEFAULTS.wide_runs,
    count_wide_as_legal: matchData.count_wide_as_legal ?? RULE_DEFAULTS.count_wide_as_legal,
    no_ball_runs: matchData.no_ball_runs ?? RULE_DEFAULTS.no_ball_runs,
    count_no_ball_as_legal: matchData.count_no_ball_as_legal ?? RULE_DEFAULTS.count_no_ball_as_legal,
    ignore_wide_noball_last_overs: matchData.ignore_wide_noball_last_overs ?? RULE_DEFAULTS.ignore_wide_noball_last_overs,

    wagon_wheel_enabled: matchData.wagon_wheel_enabled ?? true,
    wagon_wheel_dot_ball: matchData.wagon_wheel_dot_ball ?? true,
    wagon_wheel_1s_2s_3s: matchData.wagon_wheel_1s_2s_3s ?? true,
    shot_selection_enabled: matchData.shot_selection_enabled ?? true,

    schedule_match: matchData.schedule_match ?? false,
    scheduled_time: matchData.scheduled_time ?? "",
  })

  useEffect(() => {
    if (!matchData) return
    setFormData((prev) => ({
      ...prev,
      free_hit_enabled: matchData.free_hit_enabled ?? prev.free_hit_enabled,
      super_over_enabled: matchData.super_over_enabled ?? prev.super_over_enabled,
      last_batter_rule: matchData.last_batter_rule ?? prev.last_batter_rule,

      wide_runs: matchData.wide_runs ?? prev.wide_runs,
      count_wide_as_legal: matchData.count_wide_as_legal ?? prev.count_wide_as_legal,
      no_ball_runs: matchData.no_ball_runs ?? prev.no_ball_runs,
      count_no_ball_as_legal: matchData.count_no_ball_as_legal ?? prev.count_no_ball_as_legal,
      ignore_wide_noball_last_overs: matchData.ignore_wide_noball_last_overs ?? prev.ignore_wide_noball_last_overs,

      wagon_wheel_enabled: matchData.wagon_wheel_enabled ?? prev.wagon_wheel_enabled,
      wagon_wheel_dot_ball: matchData.wagon_wheel_dot_ball ?? prev.wagon_wheel_dot_ball,
      wagon_wheel_1s_2s_3s: matchData.wagon_wheel_1s_2s_3s ?? prev.wagon_wheel_1s_2s_3s,
      shot_selection_enabled: matchData.shot_selection_enabled ?? prev.shot_selection_enabled,

      schedule_match: matchData.schedule_match ?? prev.schedule_match,
      scheduled_time: matchData.scheduled_time ?? prev.scheduled_time,
    }))
  }, [matchData])

  // When the step's match data does not carry the rules (e.g. straight after creating the
  // match), read them from the server so the form shows what the match really has: the
  // 100-ball format and the tournament may have set the no-ball and wide runs.
  const [serverStatus, setServerStatus] = useState<string | null>(null)
  useEffect(() => {
    const id = matchData?.match_id
    if (!id) return
    if (matchData.wide_runs != null && matchData.no_ball_runs != null && matchData.status) return
    let cancelled = false
    ;(async () => {
      try {
        const res: any = await api.get(`/matches/${id}`)
        const m = res?.data?.match
        if (!m || cancelled) return
        setServerStatus(m.status ?? null)
        setFormData((prev) => ({
          ...prev,
          wide_runs: matchData.wide_runs ?? m.wide_runs ?? prev.wide_runs,
          count_wide_as_legal: matchData.count_wide_as_legal ?? m.count_wide_as_legal ?? prev.count_wide_as_legal,
          no_ball_runs: matchData.no_ball_runs ?? m.no_ball_runs ?? prev.no_ball_runs,
          count_no_ball_as_legal: matchData.count_no_ball_as_legal ?? m.count_no_ball_as_legal ?? prev.count_no_ball_as_legal,
          ignore_wide_noball_last_overs:
            matchData.ignore_wide_noball_last_overs ?? m.ignore_wide_noball_last_overs ?? prev.ignore_wide_noball_last_overs,
        }))
      } catch {
        // Keep the form as it is: the server defaults above are the safe fallback.
      }
    })()
    return () => { cancelled = true }
  }, [matchData?.match_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const isLive = LIVE_STATUSES.has(String(matchData.status ?? serverStatus ?? "").toLowerCase())
  const nonStandardRuns = Number(formData.wide_runs) !== 1 || Number(formData.no_ball_runs) !== 1

  const [errors, setErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  // A live match can save its rules and stay on this step instead of moving to the toss.
  const [stayAfterSave, setStayAfterSave] = useState(false)

  const validateForm = () => {
    const newErrors: any = {}

    // Wide runs validation
    if (formData.wide_runs) {
      const wideRuns = Number.parseInt(formData.wide_runs.toString())
      if (wideRuns < 1 || wideRuns > 10) {
        newErrors.wide_runs = "Wide runs must be between 1-10"
      }
    }

    // No ball runs validation
    if (formData.no_ball_runs) {
      const noBallRuns = Number.parseInt(formData.no_ball_runs.toString())
      if (noBallRuns < 1 || noBallRuns > 10) {
        newErrors.no_ball_runs = "No ball runs must be between 1-10"
      }
    }

    // Ignore wide/no ball last overs validation
    if (formData.ignore_wide_noball_last_overs) {
      const ignoreOvers = Number.parseInt(formData.ignore_wide_noball_last_overs.toString())
      if (ignoreOvers < 0 || ignoreOvers > 10) {
        newErrors.ignore_wide_noball_last_overs = "Ignore wide/no ball last overs must be between 0-10"
      }
    }

    // Scheduled time validation
    if (formData.schedule_match && !formData.scheduled_time) {
      newErrors.scheduled_time = "Scheduled time is required when scheduling is enabled"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Which button submitted: "Save rules only" stays on this step (live matches).
    const stay = (e.nativeEvent as any)?.submitter?.getAttribute?.("data-stay") === "true"
    setStayAfterSave(stay)

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      // Build payload with only the new fields
      const payload: any = {
        // Keep existing fields from first step
        pitch_type: matchData.pitch_type,
        powerplay_enabled: matchData.powerplay_enabled,
        max_overs_per_bowler: matchData.max_overs_per_bowler,
        officials_umpires: matchData.officials_umpires,
        officials_scorer: matchData.officials_scorer,
        officials_live_streamer: matchData.officials_live_streamer,
        officials_others: matchData.officials_others,

        // Add new fields from this step
        free_hit_enabled: formData.free_hit_enabled,
        super_over_enabled: formData.super_over_enabled,
        last_batter_rule: formData.last_batter_rule,
        wagon_wheel_enabled: formData.wagon_wheel_enabled,
        wagon_wheel_dot_ball: formData.wagon_wheel_dot_ball,
        wagon_wheel_1s_2s_3s: formData.wagon_wheel_1s_2s_3s,
        shot_selection_enabled: formData.shot_selection_enabled,
        count_wide_as_legal: formData.count_wide_as_legal,
        count_no_ball_as_legal: formData.count_no_ball_as_legal,
        schedule_match: formData.schedule_match,

        // Numeric fields
        wide_runs: Number.parseInt(formData.wide_runs.toString()),
        no_ball_runs: Number.parseInt(formData.no_ball_runs.toString()),
        ignore_wide_noball_last_overs: Number.parseInt(formData.ignore_wide_noball_last_overs.toString()),
      }

      if (matchData.powerplay_overs) {
        payload.powerplay_overs = matchData.powerplay_overs
      }
      // Add scheduled_time if schedule_match is true and scheduled_time has a valid value
      if (formData.schedule_match && formData.scheduled_time && formData.scheduled_time.toString().trim()) {
        payload.scheduled_time = new Date(formData.scheduled_time).toISOString()
      }

      await api.put(`/matches/${matchData.match_id}/preview-rules`, payload)
      if (stay) {
        toast.success(isLive ? "Match rules updated. They apply to balls recorded from now on." : "Match rules updated")
      } else {
        toast.success("Match rules updated successfully")
        onComplete({ ...matchData, ...formData })
      }
    } catch (error: any) {
      console.error("Network Error:", error)
      toast.error(error?.response?.data?.message || "Failed to update the match rules", { duration: 8000 })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Match Information Preview */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-blue-900 flex items-center">Match Information Preview</h3>
          <button
            type="button"
            onClick={onEditBasicInfo}
            className="flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
          >
            <FiEdit3 className="w-4 h-4 mr-1" />
            Edit
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Match Title:</span>
            <p className="text-gray-900">{matchData.title}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Match Type:</span>
            <p className="text-gray-900 capitalize">{matchData.match_type}</p>
          </div>
          <div className="flex items-center">
            <FiCalendar className="w-4 h-4 mr-1 text-gray-500" />
            <span className="font-medium text-gray-700 mr-2">Date:</span>
            <p className="text-gray-900">
              {matchData.match_date ? new Date(matchData.match_date).toLocaleDateString() : "Not set"}
            </p>
          </div>
          <div className="flex items-center">
            <span className="font-medium text-gray-700 mr-2">City:</span>
            <p className="text-gray-900">{matchData.city}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Ball Type:</span>
            <p className="text-gray-900 capitalize">{matchData.ball_type}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Total Overs:</span>
            <p className="text-gray-900">{matchData.total_overs}</p>
          </div>
          {matchData.pitch_type && (
            <div>
              <span className="font-medium text-gray-700">Pitch Type:</span>
              <p className="text-gray-900 capitalize">{matchData.pitch_type}</p>
            </div>
          )}
          {matchData.powerplay_enabled && (
            <div>
              <span className="font-medium text-gray-700">Powerplay:</span>
              <p className="text-gray-900">{matchData.powerplay_overs} overs</p>
            </div>
          )}
          {matchData.max_overs_per_bowler && (
            <div>
              <span className="font-medium text-gray-700">Max Overs/Bowler:</span>
              <p className="text-gray-900">{matchData.max_overs_per_bowler}</p>
            </div>
          )}
        </div>

        {/* Officials Preview */}
        {(matchData.officials_umpires ||
          matchData.officials_scorer ||
          matchData.officials_live_streamer ||
          matchData.officials_others) && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <span className="font-medium text-gray-700 block mb-2">Required Officials:</span>
            <div className="flex flex-wrap gap-2">
              {matchData.officials_umpires && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Umpires</span>
              )}
              {matchData.officials_scorer && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Scorer</span>
              )}
              {matchData.officials_live_streamer && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Live Streamer</span>
              )}
              {matchData.officials_others && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Others</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Advanced Game Rules Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Advanced Game Rules Section */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiSettings className="w-5 h-5 mr-2" />
            Advanced Game Rules
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormCheckbox
              label="Free Hit"
              checked={formData.free_hit_enabled}
              onChange={(checked) => setFormData((prev) => ({ ...prev, free_hit_enabled: checked }))}
              description="Free hit after no-ball"
            />

            <FormCheckbox
              label="Super Over"
              checked={formData.super_over_enabled}
              onChange={(checked) => setFormData((prev) => ({ ...prev, super_over_enabled: checked }))}
              description="Super over in case of tie"
            />
          </div>
        </div>

        {/* Scoring Rules Section */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Scoring Rules</h3>

          {isLive && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
              This match is live. Changes apply to balls recorded from now on; balls already scored keep their runs.
              Phones on app 1.0.25 get the new rules at once.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FormInput
              label="Wide Ball Runs"
              type="number"
              value={formData.wide_runs}
              onChange={(value) => setFormData((prev) => ({ ...prev, wide_runs: value }))}
              error={errors.wide_runs}
              min={1}
              max={10}
            />

            <FormInput
              label="No Ball Runs"
              type="number"
              value={formData.no_ball_runs}
              onChange={(value) => setFormData((prev) => ({ ...prev, no_ball_runs: value }))}
              error={errors.no_ball_runs}
              min={1}
              max={10}
            />

            <FormInput
              label="Ignore Wide/No Ball in Last Overs"
              type="number"
              value={formData.ignore_wide_noball_last_overs}
              onChange={(value) => setFormData((prev) => ({ ...prev, ignore_wide_noball_last_overs: value }))}
              error={errors.ignore_wide_noball_last_overs}
              min={0}
              max={10}
            />
          </div>

          {nonStandardRuns && (
            <p className="mt-4 text-xs text-gray-600">
              With wide or no-ball runs other than 1, this match can only be scored on app 1.0.25 or later. Phones on older
              versions are refused with an update message.
            </p>
          )}
        </div>

        {/* Match Features Section */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Match Features & Analytics</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormCheckbox
              label="Wagon Wheel"
              checked={formData.wagon_wheel_enabled}
              onChange={(checked) => setFormData((prev) => ({ ...prev, wagon_wheel_enabled: checked }))}
              description="Visual representation of shots"
            />

            <FormCheckbox
              label="Shot Selection"
              checked={formData.shot_selection_enabled}
              onChange={(checked) => setFormData((prev) => ({ ...prev, shot_selection_enabled: checked }))}
              description="Track different shot types"
            />

            <FormCheckbox
              label="Wagon Wheel Dot Balls"
              checked={formData.wagon_wheel_dot_ball}
              onChange={(checked) => setFormData((prev) => ({ ...prev, wagon_wheel_dot_ball: checked }))}
              description="Show dot balls on wagon wheel"
            />

            <FormCheckbox
              label="Wagon Wheel 1s, 2s, 3s"
              checked={formData.wagon_wheel_1s_2s_3s}
              onChange={(checked) => setFormData((prev) => ({ ...prev, wagon_wheel_1s_2s_3s: checked }))}
              description="Show singles, doubles, triples"
            />
          </div>
        </div>

        {/* Schedule Section */}
        {/* <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiClock className="w-5 h-5 mr-2" />
            Match Scheduling
          </h3>

          <div className="space-y-4">
            <FormCheckbox
              label="Schedule Match"
              checked={formData.schedule_match}
              onChange={(checked) => setFormData((prev) => ({ ...prev, schedule_match: checked }))}
              description="Schedule the match for a specific time"
            />

            {formData.schedule_match && (
              <div className="max-w-md">
                <FormInput
                  label="Scheduled Time"
                  type="datetime-local"
                  value={formData.scheduled_time}
                  onChange={(value) => setFormData((prev) => ({ ...prev, scheduled_time: value }))}
                  placeholder="Select scheduled time"
                  error={errors.scheduled_time}
                  required={formData.schedule_match}
                />
              </div>
            )}
          </div>
        </div> */}

        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Back to Player Selection
          </button>
          <div className="flex gap-3">
            {isLive && (
              <button
                type="submit"
                disabled={loading}
                data-stay="true"
                className="px-6 py-2 border border-primary-600 text-primary-700 rounded-md hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && stayAfterSave ? "Saving Rules..." : "Save rules only"}
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              data-stay="false"
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && !stayAfterSave ? "Saving Rules..." : "Continue to Toss"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
